import type { WireTaskCapability, WireTaskTool } from '@/api/wire';
import type { Tone } from '@/ui/primitives';

/**
 * A twin's task in words (Amendment 0011, ADR-0139), shared by the plan in
 * the side chat, the person's task page and the superadmin's Workspace, so
 * the three say the same thing about the same tool.
 */

/** Where a tool stands for a plan. Only `needs_person` waits on somebody; `unavailable` waits on setup. */
export const TOOL_STATUS: Record<WireTaskTool['status'], { label: string; tone: Tone }> = {
  usable: { label: 'USABLE', tone: 'accent' },
  needs_person: { label: 'NEEDS A SUPERADMIN', tone: 'warning' },
  unavailable: { label: 'SET UP FIRST', tone: 'neutral' },
};

export const VIA: Record<'held' | 'earned', string> = {
  held: 'you hold it',
  earned: 'granted on its record',
};

export const CAPABILITY_STATE: Record<WireTaskCapability['state'], { label: string; tone: Tone }> = {
  held: { label: 'HELD', tone: 'accent' },
  granted: { label: 'GRANTED', tone: 'accent' },
  requested: { label: 'WAITING ON A SUPERADMIN', tone: 'warning' },
  unavailable: { label: 'SET UP FIRST', tone: 'neutral' },
  refused: { label: 'REFUSED', tone: 'danger' },
};

export const GRANT_BASIS: Record<'supervised' | 'earned', string> = {
  supervised: 'granted by a superadmin',
  earned: 'granted on its record',
};

const TASK_STATE: Record<string, { label: string; tone: Tone }> = {
  open: { label: 'TO DO', tone: 'neutral' },
  in_progress: { label: 'RUNNING', tone: 'accent' },
  waiting: { label: 'WAITING ON A TOOL', tone: 'warning' },
  done: { label: 'DONE', tone: 'current' },
  cancelled: { label: 'CANCELLED', tone: 'neutral' },
};

export function taskState(state: string, withYou = false): { label: string; tone: Tone } {
  // Handed back reads differently from queued: it is the person's to do now (ADR-0151).
  if (state === 'open' && withYou) return { label: 'BACK WITH YOU', tone: 'warning' };
  return TASK_STATE[state] ?? { label: state.replace(/_/g, ' ').toUpperCase(), tone: 'neutral' };
}

/** A source the task read, as a person weighs it: the page itself said so, or only a search excerpt did. */
export function sourceLabel(verified: boolean | null): string {
  return verified ? 'page read back — verified' : 'unverified excerpt';
}

/** The record toward the platform granting a tool by itself (Amendment 0011, point 3). */
export function recordLine(record: { clean: number; needed?: number; stopped: boolean }, needed: number): string {
  const toward = `${record.clean} of ${record.needed ?? needed} toward granting it by itself`;
  return record.stopped ? `${toward} · earned granting is stopped for it` : toward;
}
